import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { sectionsApi } from '../services/api';
import { Section } from '../types';
import {
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  Layout,
  GripVertical,
  X
} from 'lucide-react';

// Sortable Section Item
function SortableSection({
  section,
  onEdit,
  onDelete
}: {
  section: Section;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg border border-gray-200 p-4 flex items-center"
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 mr-3 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5" />
      </button>
      
      <div className="flex-1">
        <div className="flex items-center">
          <span className="text-lg font-medium text-gray-900">
            §{section.sortOrder + 1}
          </span>
          <h3 className="ml-3 font-medium text-gray-900">{section.name}</h3>
        </div>
        {section.description && (
          <p className="text-sm text-gray-500 mt-1 ml-9">{section.description}</p>
        )}
        <p className="text-xs text-gray-400 mt-1 ml-9">
          {section._count?.blocks || 0} Bauteil(e)
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={onEdit}
          className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
          title="Bearbeiten"
        >
          <Edit className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
          title="Löschen"
          disabled={(section._count?.blocks || 0) > 0}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function AdminSections() {
  const [sections, setSections] = useState<Section[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [hasUnsavedOrder, setHasUnsavedOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const { sections } = await sectionsApi.getAll();
      setSections(sections);
    } catch (err) {
      setError('Fehler beim Laden der Abteile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update sortOrder
        return newItems.map((item, index) => ({ ...item, sortOrder: index }));
      });
      setHasUnsavedOrder(true);
    }
  };

  const handleSaveOrder = async () => {
    try {
      await sectionsApi.reorder(
        sections.map((s, index) => ({ id: s.id, sortOrder: index }))
      );
      setHasUnsavedOrder(false);
    } catch (err) {
      setError('Fehler beim Speichern der Reihenfolge');
    }
  };

  const handleDelete = async (id: string) => {
    const section = sections.find(s => s.id === id);
    if (section && (section._count?.blocks || 0) > 0) {
      alert('Abteil kann nicht gelöscht werden, da noch Bauteile zugeordnet sind.');
      return;
    }
    
    if (!window.confirm('Abteil wirklich löschen?')) return;
    
    try {
      await sectionsApi.delete(id);
      setSections(sections.filter(s => s.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Abteile verwalten</h1>
          <p className="mt-1 text-sm text-gray-500">
            Definieren Sie die Kategorien für Vertragsinhalte (werden als §1, §2, ... sortiert)
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          {hasUnsavedOrder && (
            <button
              onClick={handleSaveOrder}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
            >
              Reihenfolge speichern
            </button>
          )}
          <button
            onClick={() => {
              setEditingSection(null);
              setShowModal(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neues Abteil
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Info */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
        <strong>Tipp:</strong> Ziehen Sie die Abteile per Drag & Drop in die gewünschte Reihenfolge.
        Die Reihenfolge bestimmt die §-Nummerierung im Vertrag.
      </div>

      {/* Sections List */}
      {sections.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Layout className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Abteile vorhanden</h3>
          <p className="text-gray-500 mb-4">Erstellen Sie Ihr erstes Abteil</p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Abteil erstellen
          </button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map(s => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {sections.map((section) => (
                <SortableSection
                  key={section.id}
                  section={section}
                  onEdit={() => {
                    setEditingSection(section);
                    setShowModal(true);
                  }}
                  onDelete={() => handleDelete(section.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Section Modal */}
      {showModal && (
        <SectionModal
          section={editingSection}
          onClose={() => {
            setShowModal(false);
            setEditingSection(null);
          }}
          onSave={async () => {
            await loadSections();
            setShowModal(false);
            setEditingSection(null);
          }}
        />
      )}
    </div>
  );
}

// Section Modal Component
function SectionModal({
  section,
  onClose,
  onSave
}: {
  section: Section | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const [formData, setFormData] = useState({
    name: section?.name || '',
    description: section?.description || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Name erforderlich');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (section) {
        await sectionsApi.update(section.id, formData);
      } else {
        await sectionsApi.create(formData);
      }
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {section ? 'Abteil bearbeiten' : 'Neues Abteil'}
            </h2>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="z.B. Mietzins"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Beschreibung
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Optionale Beschreibung des Abteils"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Wird gespeichert...' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
